import React, { useState, useEffect } from 'react';
import { auth, db } from '../lib/firebase';
import { signOut } from 'firebase/auth';
import { collection, query, where, onSnapshot, addDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

function Dashboard() {
  const [user, setUser] = useState(null);
  const [apps, setApps] = useState([]);
  const [showNewApp, setShowNewApp] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate('/dashboard');
        return;
      }
      
      // Check email verification for email/password users
      if (user.providerData[0]?.providerId === 'password' && !user.emailVerified) {
        alert('Please verify your email to continue. Check your inbox!');
        // Optionally sign them out
        // signOut(auth);
        // return;
      }
      
      setUser(user);
    });
    return unsubscribe;
  }, [navigate]);

  useEffect(() => {
    if (user) {
      const q = query(collection(db, 'apps'), where('ownerId', '==', user.uid));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const appsData = [];
        snapshot.forEach((doc) => {
          appsData.push({ id: doc.id, ...doc.data() });
        });
        setApps(appsData);
      });
      return unsubscribe;
    }
  }, [user]);

  const createApp = async () => {
    if (!prompt.trim()) return;
    
    setLoading(true);
    try {
      await addDoc(collection(db, 'apps'), {
        name: prompt.substring(0, 50),
        description: prompt,
        ownerId: user.uid,
        status: 'draft',
        createdAt: new Date(),
        template: 'blog'
      });
      setShowNewApp(false);
      setPrompt('');
    } catch (error) {
      console.error('Error creating app:', error);
    }
    setLoading(false);
  };

  const handleSignOut = () => {
    signOut(auth);
    navigate('/');
  };

  if (!user) return null;

  return (
    <div className="dashboard-container">
      <div className="dashboard-bg"></div>
      
      <nav className="dashboard-nav">
        <h1 className="dashboard-logo">Scalesman</h1>
        <div className="user-info">
          <img src={user.photoURL} alt="Profile" className="user-avatar" />
          <span>{user.displayName}</span>
          <button onClick={handleSignOut} className="sign-out-btn">Sign Out</button>
        </div>
      </nav>

      <div className="dashboard-content">
        <h2 className="dashboard-title">Your Apps</h2>
        
        {!showNewApp ? (
          <button onClick={() => setShowNewApp(true)} className="create-app-btn">
            + Create New App
          </button>
        ) : (
          <div className="new-app-modal">
            <h3>Describe Your App</h3>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="I want to build a food blog with recipes and photos..."
              className="app-prompt"
            />
            <div className="modal-buttons">
              <button onClick={createApp} disabled={loading} className="generate-btn">
                {loading ? 'Creating...' : 'Generate App'}
              </button>
              <button onClick={() => setShowNewApp(false)} className="cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="apps-grid">
          {apps.length === 0 && !showNewApp && (
            <div className="empty-state">
              <h3>No apps yet</h3>
              <p>Create your first app to get started!</p>
            </div>
          )}
          
          {apps.map(app => (
            <div key={app.id} className="app-card">
              <div className="app-icon">🚀</div>
              <h3>{app.name}</h3>
              <p className="app-status">Status: {app.status}</p>
              <div className="app-actions">
                <button 
                  className="edit-btn"
                  onClick={() => navigate(`/builder/${app.id}`)}
                >
                  Edit
                </button>
                {app.url && (
                  <a href={app.url} target="_blank" rel="noopener noreferrer" className="view-btn">
                    View Live
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;